import React, { useState, useEffect } from 'react';
import Loader from '../../Common/Loader';
import { CustomDropdown } from '../../Common/CustomDropdown';
import { CustomCalendar } from '../../Common/CustomCalendar';
import { getZonesAPI, getCitiesAPI, getDeposAPI } from '../../../api/locations';
import { getOrders, mapBackendOrderToFrontend } from '../../../api/orders';
import { ENDPOINTS } from '../../../utils/endpoints';
import { CSVButton } from '../../Common/CSVButton';
import { exportToCSV } from '../../../utils/csvExport';
import { Eye, Edit2, X, Package, MapPin, Tag, Truck, AlertCircle, FileSpreadsheet, Download } from 'lucide-react';

export interface LineItem {
  id: string;
  name: string;
  sku: string;
  unitPrice: number;
  qty: number;
  unit: string;
  amount: number;
  scheme?: string;
  schemeBonus?: string;
  schemeObj?: any;
  incentive?: string;
  tax?: string;
  status?: string;
  iconBg?: string;
  iconText?: string;
}

export interface OrderItem {
  id: string;
  orderId: string;
  date: string;
  customerName: string;
  distributorSapCode?: string;
  zone?: string;
  city?: string;
  itemsSummary: string;
  extraItemsCount?: number;
  qty: number;
  totalAmount: number;
  scheme?: string;
  schemeBonus?: string;
  appliedSchemes?: any[];
  incentive?: string;
  status: 'DELIVERED' | 'PARTIAL' | 'DISPATCHED' | 'PENDING' | 'CANCELLED';
  lineItems: LineItem[];
  expectedDeliveryDate?: string;
  shippingAddress?: string;
  specialInstructions?: string;
}

export interface OrderReportProps {
  initialDistributorName?: string;
  showFiltersHeader?: boolean;
}

const MOCK_ORDERS: OrderItem[] = [
  {
    id: '1',
    orderId: 'GOW-2025-091',
    date: '15 Jan 2025',
    customerName: 'Maurya Enterprises-Malad Distributor',
    distributorSapCode: 'SAP-100234',
    zone: 'West',
    city: 'Mumbai',
    itemsSummary: 'Gowardhan Ghee 1L',
    qty: 10,
    totalAmount: 18500,
    scheme: '19+1 Free',
    schemeBonus: '+10 Free',
    incentive: '₹1,200 Month end',
    status: 'DELIVERED',
    expectedDeliveryDate: '16 Jan 2025',
    shippingAddress: 'Plot No. 12, Sector 4, Industrial Area, Pune, MH, 411018',
    specialInstructions: 'Ship via refrigerated container. Keep temperature strictly between 2°C and 4°C.',
    lineItems: [
      {
        id: 'li-101',
        name: 'Gowardhan Ghee 1L Jar',
        sku: 'GOW-GHEE-1000',
        unitPrice: 1850,
        qty: 10,
        unit: 'units',
        amount: 18500,
        scheme: '19+1 Free',
        schemeBonus: '+10 Free',
        incentive: '₹1,200 Month end',
        tax: 'Inclusive (5%)',
        status: 'DELIVERED',
        iconBg: 'bg-amber-100',
        iconText: '🧀'
      }
    ]
  },
  {
    id: '2',
    orderId: 'GOW-2025-092',
    date: '15 Jan 2025',
    customerName: 'Shree Balaji Distributors',
    distributorSapCode: 'SAP-100582',
    zone: 'North',
    city: 'Delhi',
    itemsSummary: 'Toned Milk 500ml',
    extraItemsCount: 2,
    qty: 250,
    totalAmount: 38200,
    scheme: '9+1 Free',
    schemeBonus: '+3 Free',
    incentive: '₹1,200 Month end',
    status: 'PARTIAL',
    expectedDeliveryDate: '18 Jan 2025',
    shippingAddress: 'Plot No. 12, Sector 4, Industrial Area, Pune, MH, 411018',
    specialInstructions: 'Ship via refrigerated container. Keep temperature strictly between 2°C and 4°C. Handle with extreme care.',
    lineItems: [
      {
        id: 'li-201',
        name: 'Toned Milk 500ml pouch',
        sku: 'GOW-MILK-500',
        unitPrice: 71.0,
        qty: 200,
        unit: 'units',
        amount: 14200,
        scheme: '19+1 Free',
        schemeBonus: '+1 Free',
        incentive: '₹1,200 Month end',
        tax: 'Inclusive (5%)',
        status: 'DELIVERED',
        iconBg: 'bg-blue-100',
        iconText: '🥛'
      },
      {
        id: 'li-202',
        name: 'Paneer Classic 200g',
        sku: 'GOW-PAN-200',
        unitPrice: 400.0,
        qty: 30,
        unit: 'units',
        amount: 12000,
        scheme: '—',
        incentive: '—',
        tax: 'Inclusive (12%)',
        status: 'PENDING',
        iconBg: 'bg-amber-100',
        iconText: '🧀'
      }
    ]
  }
];

export const OrderReport: React.FC<OrderReportProps> = ({
  initialDistributorName,
  showFiltersHeader = true,
}) => {
  const [orders, setOrders] = useState<OrderItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Filters
  const [zones, setZones] = useState<string[]>(['All']);
  const [selectedZone, setSelectedZone] = useState<string>('All');
  const [zoneIdMap, setZoneIdMap] = useState<Record<string, number>>({});

  const [cities, setCities] = useState<string[]>(['All']);
  const [selectedCity, setSelectedCity] = useState<string>('All');
  const [, setCityIdMap] = useState<Record<string, number>>({});

  const [selectedStatus, setSelectedStatus] = useState<string>('All');
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Modals
  const [viewingOrderDetails, setViewingOrderDetails] = useState<OrderItem | null>(null);

  // Export Modals State
  const [isSapModalOpen, setIsSapModalOpen] = useState(false);
  const [sapStartDate, setSapStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [sapEndDate, setSapEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [isExportingSap, setIsExportingSap] = useState(false);
  const [sapError, setSapError] = useState<string | null>(null);

  const [isRouteModalOpen, setIsRouteModalOpen] = useState(false);
  const [deposList, setDeposList] = useState<{ id: number; name: string }[]>([]);
  const [selectedDepoId, setSelectedDepoId] = useState<string | number>('');
  const [routeDate, setRouteDate] = useState(new Date().toISOString().split('T')[0]);
  const [isExportingRoute, setIsExportingRoute] = useState(false);
  const [routeError, setRouteError] = useState<string | null>(null);

  // Fetch Depos for Route Sheet Export
  useEffect(() => {
    async function loadDepos() {
      try {
        const res = await getDeposAPI();
        const rawList = res?.data || res?.depos || (Array.isArray(res) ? res : null);
        if (Array.isArray(rawList) && rawList.length > 0) {
          const mapped = rawList.map((d: any) => ({ id: d.id, name: d.name || `Depo #${d.id}` }));
          setDeposList(mapped);
          setSelectedDepoId(mapped[0].id);
        } else {
          const fallback = [
            { id: 4, name: 'ANAND AGRO-PACKING STATION VASHI' },
            { id: 6, name: 'BANGLORE DEPOT MILK' },
            { id: 7, name: 'CHENNAI DEPOT MILK - Chennai' },
            { id: 2, name: 'GAUGANGA NAGPUR' },
          ];
          setDeposList(fallback);
          setSelectedDepoId(fallback[0].id);
        }
      } catch (err) {
        console.warn('Failed to fetch depos for route sheet modal:', err);
        const fallback = [
          { id: 4, name: 'ANAND AGRO-PACKING STATION VASHI' },
          { id: 6, name: 'BANGLORE DEPOT MILK' },
          { id: 7, name: 'CHENNAI DEPOT MILK - Chennai' },
          { id: 2, name: 'GAUGANGA NAGPUR' },
        ];
        setDeposList(fallback);
        setSelectedDepoId(fallback[0].id);
      }
    }
    loadDepos();
  }, []);

  // Fetch Zones
  useEffect(() => {
    async function loadZones() {
      try {
        const res = await getZonesAPI();
        if (res?.status === 'success' && Array.isArray(res.data)) {
          const zMap: Record<string, number> = {};
          const zNames: string[] = ['All'];
          res.data.forEach((z: any) => {
            zNames.push(z.name);
            zMap[z.name] = z.id;
          });
          setZones(zNames);
          setZoneIdMap(zMap);
        }
      } catch (err) {
        console.error('Failed to fetch zones for orders', err);
      }
    }
    loadZones();
  }, []);

  // Fetch Cities dependent on Zone
  useEffect(() => {
    async function loadCities() {
      try {
        const zId = selectedZone !== 'All' ? zoneIdMap[selectedZone] : undefined;
        const res = await getCitiesAPI(zId);
        if (res?.status === 'success' && Array.isArray(res.data)) {
          const cMap: Record<string, number> = {};
          const cNames: string[] = ['All'];
          res.data.forEach((c: any) => {
            cNames.push(c.name);
            cMap[c.name] = c.id;
          });
          setCities(cNames);
          setCityIdMap(cMap);
        }
      } catch (err) {
        console.error('Failed to fetch cities for orders', err);
      }
    }
    loadCities();
  }, [selectedZone, zoneIdMap]);

  // Fetch Orders
  useEffect(() => {
    async function loadOrders() {
      setLoading(true);
      try {
        const res = await getOrders({
          status: selectedStatus !== 'All' ? selectedStatus : undefined,
        });

        if (res?.status === 'success' && res.data) {
          const rawList = Array.isArray(res.data)
            ? res.data
            : res.data.orders || [];

          if (rawList.length > 0) {
            const mapped = rawList.map(mapBackendOrderToFrontend);
            setOrders(mapped);
          } else {
            setOrders(MOCK_ORDERS);
          }
        } else {
          setOrders(MOCK_ORDERS);
        }
      } catch (err) {
        console.error('Error fetching orders, using mock data:', err);
        setOrders(MOCK_ORDERS);
      } finally {
        setLoading(false);
      }
    }
    loadOrders();
  }, [selectedStatus, selectedZone, selectedCity]);

  // Client filtering
  const filteredOrders = orders.filter((order) => {
    if (initialDistributorName && !order.customerName.toLowerCase().includes(initialDistributorName.toLowerCase())) {
      return false;
    }
    if (selectedZone !== 'All' && order.zone && order.zone.toLowerCase() !== selectedZone.toLowerCase()) {
      return false;
    }
    if (selectedCity !== 'All' && order.city && order.city.toLowerCase() !== selectedCity.toLowerCase()) {
      return false;
    }
    if (selectedStatus !== 'All' && order.status.toUpperCase() !== selectedStatus.toUpperCase()) {
      return false;
    }
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      const matchId = order.orderId.toLowerCase().includes(term);
      const matchCustomer = order.customerName.toLowerCase().includes(term);
      const matchItem = order.itemsSummary.toLowerCase().includes(term);
      const matchSap = order.distributorSapCode?.toLowerCase().includes(term);
      if (!matchId && !matchCustomer && !matchItem && !matchSap) return false;
    }
    return true;
  });

  const getStatusBadge = (status: OrderItem['status']) => {
    return <span className="text-xs text-[#828282]">{status || '-'}</span>;
  };

  const downloadOrderExport = async (endpoint: string, params: Record<string, string>, defaultFileName: string) => {
    const query = new URLSearchParams(params).toString();
    const baseUrl = import.meta.env.VITE_GOWARDHAN_ERP_BASE_URL || '';
    const cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const url = `${cleanBase}${cleanEndpoint}?${query}`;
    const token = localStorage.getItem('gowardhan_erp_token');

    const res = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'ngrok-skip-browser-warning': 'true',
      },
    });

    if (!res.ok) {
      let errorMsg = `Download failed with status ${res.status}`;
      try {
        const json = await res.json();
        errorMsg = json.message || errorMsg;
      } catch {
        try {
          const text = await res.text();
          if (text) errorMsg = text;
        } catch {
          // Fallback parsing failed; retain default error message
        }
      }
      throw new Error(errorMsg);
    }

    const blob = await res.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = defaultFileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(downloadUrl);
  };

  const handleExportSapSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sapStartDate || !sapEndDate) {
      setSapError('Please select both Start Date and End Date.');
      return;
    }
    setSapError(null);
    setIsExportingSap(true);
    try {
      const endpoint = ENDPOINTS.EXPORT_SAP_ORDERS;
      const params = { startDate: sapStartDate, endDate: sapEndDate };
      const filename = `orders_sap_export_${sapStartDate}_to_${sapEndDate}.xlsx`;
      await downloadOrderExport(endpoint, params, filename);
      setIsSapModalOpen(false);
    } catch (err: any) {
      console.error('Failed to export SAP orders:', err);
      setSapError(err.message || 'Export failed. Please check backend connection.');
    } finally {
      setIsExportingSap(false);
    }
  };

  const handleExportRouteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDepoId) {
      setRouteError('Please select a Depo.');
      return;
    }
    if (!routeDate) {
      setRouteError('Please select a Date.');
      return;
    }
    setRouteError(null);
    setIsExportingRoute(true);
    try {
      const endpoint = ENDPOINTS.EXPORT_DELIVERY_ROUTE_SHEET;
      const params = { depoId: String(selectedDepoId), date: routeDate };
      const depoName = deposList.find(d => String(d.id) === String(selectedDepoId))?.name || 'depo';
      const filename = `delivery_route_sheet_${depoName.replace(/\s+/g, '_')}_${routeDate}.xlsx`;
      await downloadOrderExport(endpoint, params, filename);
      setIsRouteModalOpen(false);
    } catch (err: any) {
      console.error('Failed to export delivery route sheet:', err);
      setRouteError(err.message || 'Export failed. Please check backend connection.');
    } finally {
      setIsExportingRoute(false);
    }
  };


  const handleExportCSV = () => {
    const headers = ['Order ID', 'Date', 'Customer Name', 'SAP Code', 'Items Summary', 'Quantity', 'Total Amount', 'Applied Scheme', 'Status'];
    const rows = filteredOrders.map((order) => [
      order.orderId,
      order.date,
      order.customerName,
      order.distributorSapCode || '',
      order.itemsSummary,
      order.qty,
      order.totalAmount,
      order.scheme || '—',
      order.status || '',
    ]);
    exportToCSV(`orders_report_${selectedStatus.toLowerCase()}`, headers, rows);
  };

  return (
    <div className="w-full space-y-6">
      {/* Header & Filters */}
      {showFiltersHeader && (
        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-xs space-y-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-black text-slate-800 tracking-tight">Orders Report</h2>
              <p className="text-xs font-semibold text-slate-400 mt-0.5">
                Track, filter, and review distributor orders & applied schemes
              </p>
            </div>
            <div className="flex items-center gap-2.5 flex-wrap">
              <input
                type="text"
                placeholder="Search order ID, distributor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-3.5 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C62828] w-56"
              />
              <CSVButton onClick={handleExportCSV} />
              <button
                type="button"
                onClick={() => {
                  setSapError(null);
                  setIsSapModalOpen(true);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#F9C327] hover:bg-[#e2af20] text-slate-900 font-bold text-xs rounded-xl shadow-2xs transition-all cursor-pointer whitespace-nowrap"
                title="Export Orders for SAP"
              >
                <FileSpreadsheet className="w-4 h-4 text-slate-900" />
                Export SAP Orders
              </button>
              <button
                type="button"
                onClick={() => {
                  setRouteError(null);
                  setIsRouteModalOpen(true);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#C62828] hover:bg-[#a81e1e] text-white font-bold text-xs rounded-xl shadow-2xs transition-all cursor-pointer whitespace-nowrap"
                title="Export Delivery Route Sheet"
              >
                <Truck className="w-4 h-4 text-white" />
                Export Route Sheet
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 border-t border-slate-100">
            <div>
              <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                Zone
              </label>
              <CustomDropdown
                options={zones}
                value={selectedZone}
                onChange={(val: string) => {
                  setSelectedZone(val);
                  setSelectedCity('All');
                }}
              />
            </div>

            <div>
              <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                City
              </label>
              <CustomDropdown
                options={cities}
                value={selectedCity}
                onChange={(val: string) => setSelectedCity(val)}
              />
            </div>

            <div>
              <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                Status
              </label>
              <CustomDropdown
                options={['All', 'PENDING', 'DISPATCHED', 'DELIVERED', 'PARTIAL', 'CANCELLED']}
                value={selectedStatus}
                onChange={(val: string) => setSelectedStatus(val)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Orders Table */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-xs overflow-hidden">
        {loading ? (
          <div className="p-8 flex justify-center">
            <Loader text="Loading orders..." />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100 text-[11px] font-extrabold uppercase text-slate-400 tracking-wider">
                  <th className="py-4 px-4">Order Details</th>
                  <th className="py-4 px-4">Distributor</th>
                  <th className="py-4 px-4">Items Summary</th>
                  <th className="py-4 px-4 text-right">Total Amount</th>
                  <th className="py-4 px-4">Applied Scheme</th>
                  <th className="py-4 px-4">Status</th>
                  <th className="py-4 px-4 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs">
                {filteredOrders.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-10 text-slate-400 font-medium">
                      No orders found matching the criteria.
                    </td>
                  </tr>
                ) : (
                  filteredOrders.map((order) => (
                    <tr key={order.id} className="hover:bg-slate-50/70 transition-colors">
                      <td className="py-3.5 px-4">
                        <span className="font-bold text-slate-800 block">{order.orderId}</span>
                        <span className="text-[11px] text-slate-400 font-medium">{order.date}</span>
                      </td>

                      <td className="py-3.5 px-4">
                        <span className="font-bold text-slate-800 block">{order.customerName}</span>
                        {order.distributorSapCode && (
                          <span className="text-[10px] text-slate-400 font-mono block">SAP: {order.distributorSapCode}</span>
                        )}
                      </td>

                      <td className="py-3.5 px-4">
                        <span className="font-semibold text-slate-700 block">{order.itemsSummary}</span>
                        <span className="text-[11px] text-slate-400">Qty: {order.qty} units {order.extraItemsCount ? `(+${order.extraItemsCount} more)` : ''}</span>
                      </td>

                      <td className="py-3.5 px-4 text-right">
                        <span className="font-extrabold text-slate-900 block">₹{order.totalAmount.toLocaleString('en-IN')}</span>
                      </td>

                      <td className="py-3.5 px-4 max-w-xs">
                        {order.scheme && order.scheme !== '—' ? (
                          <div className="flex flex-col gap-0.5">
                            <span className="font-semibold text-slate-900 text-xs leading-snug">
                              {order.scheme}
                            </span>
                            {order.schemeBonus && (
                              <span className="text-[11px] text-emerald-700 font-bold">
                                {order.schemeBonus}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400 font-medium">—</span>
                        )}
                      </td>

                      <td className="py-3.5 px-4">
                        {getStatusBadge(order.status)}
                      </td>

                      <td className="py-3.5 px-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => setViewingOrderDetails(order)}
                            className="p-1.5 text-slate-500 hover:text-[#B71C1C] transition-colors cursor-pointer rounded-lg"
                            title="View"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setViewingOrderDetails(order)}
                            className="p-1.5 text-slate-500 hover:text-blue-600 transition-colors cursor-pointer rounded-lg"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* FULL ORDER DETAILS MODAL */}
      {viewingOrderDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl max-w-4xl w-full shadow-2xl overflow-hidden border border-slate-100 max-h-[90vh] flex flex-col my-auto">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-slate-900 to-slate-800 p-5 text-white flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-white/10 rounded-2xl backdrop-blur-md">
                  <Package className="w-6 h-6 text-[#F9C327]" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-black tracking-tight">{viewingOrderDetails.orderId}</h2>
                    {getStatusBadge(viewingOrderDetails.status)}
                  </div>
                  <p className="text-xs text-slate-300 font-medium mt-0.5">
                    Order Date: {viewingOrderDetails.date}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setViewingOrderDetails(null)}
                className="p-2 text-slate-400 hover:text-white hover:bg-white/10 rounded-full transition-colors cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6 overflow-y-auto grow">
              {/* Order Info & Distributor Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                  <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block mb-1">
                    Distributor Name
                  </span>
                  <p className="text-sm font-bold text-slate-900">{viewingOrderDetails.customerName}</p>
                  {viewingOrderDetails.distributorSapCode && (
                    <p className="text-xs font-mono font-medium text-slate-500 mt-0.5">
                      SAP Code: {viewingOrderDetails.distributorSapCode}
                    </p>
                  )}
                </div>

                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                  <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block mb-1">
                    Location / Region
                  </span>
                  <p className="text-sm font-bold text-slate-900">
                    {viewingOrderDetails.city || '—'}, {viewingOrderDetails.zone || '—'}
                  </p>
                  <p className="text-xs font-medium text-slate-500 mt-0.5">Zone: {viewingOrderDetails.zone || 'All'}</p>
                </div>

                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                  <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block mb-1">
                    Order Summary
                  </span>
                  <p className="text-base font-black text-slate-900">₹{viewingOrderDetails.totalAmount.toLocaleString('en-IN')}</p>
                  <p className="text-xs font-medium text-slate-500 mt-0.5">Total Quantity: {viewingOrderDetails.qty} units</p>
                </div>
              </div>

              {/* Applied Scheme Details (If Any) */}
              {((viewingOrderDetails.appliedSchemes && viewingOrderDetails.appliedSchemes.length > 0) || (viewingOrderDetails.scheme && viewingOrderDetails.scheme !== '—')) && (
                <div className="space-y-3">
                  <h3 className="text-xs font-extrabold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                    <Tag size={14} className="text-emerald-600" /> Applied Order Schemes
                  </h3>
                  <div className="grid grid-cols-1 gap-3">
                    {viewingOrderDetails.appliedSchemes && viewingOrderDetails.appliedSchemes.length > 0 ? (
                      viewingOrderDetails.appliedSchemes.map((sch: any, idx: number) => (
                        <div key={idx} className="bg-emerald-50/70 border border-emerald-200/80 p-4 rounded-2xl space-y-2">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <span className="text-[10px] font-bold text-emerald-800 uppercase tracking-wider block">Scheme #{idx + 1}</span>
                              <h4 className="text-sm font-bold text-slate-900 mt-0.5">{sch.name}</h4>
                            </div>
                            {sch.schemeType && (
                              <span className="text-[10px] font-bold px-2 py-0.5 bg-emerald-200 text-emerald-900 rounded-md shrink-0">
                                {sch.schemeType}
                              </span>
                            )}
                          </div>
                          {sch.description && (
                            <p className="text-xs text-slate-600 font-medium bg-white/70 p-2.5 rounded-xl border border-emerald-100">
                              {sch.description}
                            </p>
                          )}
                          <div className="flex flex-wrap gap-4 pt-1 text-xs">
                            {sch.purchaseQuantity !== undefined && (
                              <div><span className="text-slate-400 font-medium">Buy:</span> <strong className="text-slate-800">{sch.purchaseQuantity} units</strong></div>
                            )}
                            {sch.freeQuantity !== undefined && (
                              <div><span className="text-emerald-700 font-medium">Free:</span> <strong className="text-emerald-800">+{sch.freeQuantity} units FREE</strong></div>
                            )}
                            {sch.crateUnits !== undefined && (
                              <div><span className="text-slate-400 font-medium">Crate Units:</span> <strong className="text-slate-800">{sch.crateUnits}</strong></div>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="bg-emerald-50/70 border border-emerald-200/80 p-4 rounded-2xl">
                        <h4 className="text-sm font-bold text-emerald-900">{viewingOrderDetails.scheme}</h4>
                        {viewingOrderDetails.schemeBonus && (
                          <p className="text-xs font-semibold text-emerald-700 mt-1">{viewingOrderDetails.schemeBonus}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Line Items Table */}
              <div className="space-y-3">
                <h3 className="text-xs font-extrabold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                  <Package size={14} className="text-slate-600" /> Order Items ({viewingOrderDetails.lineItems.length})
                </h3>
                <div className="border border-slate-100 rounded-2xl overflow-hidden shadow-2xs">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-100 text-[10px] font-extrabold uppercase text-slate-400 tracking-wider">
                        <th className="py-3 px-4">Product Name</th>
                        <th className="py-3 px-4 text-center">SKU</th>
                        <th className="py-3 px-4 text-right">Unit Price</th>
                        <th className="py-3 px-4 text-center">Qty</th>
                        <th className="py-3 px-4 text-right">Total</th>
                        <th className="py-3 px-4">Item Scheme</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-xs">
                      {viewingOrderDetails.lineItems.map((item, index) => (
                        <tr key={item.id || index} className="hover:bg-slate-50/50">
                          <td className="py-3 px-4 font-bold text-slate-800">
                            {item.name}
                          </td>
                          <td className="py-3 px-4 text-center font-mono text-[11px] text-slate-500">
                            {item.sku || '—'}
                          </td>
                          <td className="py-3 px-4 text-right font-medium text-slate-700">
                            ₹{item.unitPrice ? item.unitPrice.toFixed(2) : '0.00'}
                          </td>
                          <td className="py-3 px-4 text-center font-bold text-slate-800">
                            {item.qty} {item.unit || 'units'}
                          </td>
                          <td className="py-3 px-4 text-right font-bold text-slate-900">
                            ₹{item.amount ? item.amount.toLocaleString('en-IN') : '0'}
                          </td>
                          <td className="py-3 px-4">
                            {item.scheme && item.scheme !== '—' ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[10px] font-bold rounded-md border border-emerald-200">
                                {item.scheme} {item.schemeBonus ? `(${item.schemeBonus})` : ''}
                              </span>
                            ) : (
                              <span className="text-slate-400 text-[11px]">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Delivery & Address Information */}
              {(viewingOrderDetails.shippingAddress || viewingOrderDetails.specialInstructions || viewingOrderDetails.expectedDeliveryDate) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                  <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-1">
                    <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block flex items-center gap-1">
                      <MapPin size={12} /> Shipping Address
                    </span>
                    <p className="text-xs font-medium text-slate-700 leading-relaxed">
                      {viewingOrderDetails.shippingAddress || 'N/A'}
                    </p>
                  </div>

                  <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-1">
                    <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block flex items-center gap-1">
                      <Truck size={12} /> Delivery Details
                    </span>
                    <p className="text-xs text-slate-700">
                      Expected: <strong className="font-bold text-slate-900">{viewingOrderDetails.expectedDeliveryDate || 'N/A'}</strong>
                    </p>
                    {viewingOrderDetails.specialInstructions && (
                      <p className="text-[11px] text-slate-500 font-medium italic mt-1">
                        "{viewingOrderDetails.specialInstructions}"
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-slate-100 flex justify-end bg-slate-50 rounded-b-3xl">
              <button
                onClick={() => setViewingOrderDetails(null)}
                className="px-6 py-2 bg-slate-800 hover:bg-slate-900 text-white font-bold text-xs rounded-xl cursor-pointer transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal 1: Export Orders for SAP */}
      {isSapModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl space-y-5 border border-slate-100">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-2xl bg-[#FFF8E1] flex items-center justify-center text-[#DCA10D]">
                  <FileSpreadsheet className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-extrabold text-slate-900 tracking-tight">Export Orders for SAP</h3>
                  <p className="text-xs text-slate-400 font-medium">Select date range for SAP report</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsSapModalOpen(false)}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {sapError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs font-semibold flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0 text-red-500" />
                <span>{sapError}</span>
              </div>
            )}

            <form onSubmit={handleExportSapSubmit} className="space-y-4">
              <CustomCalendar
                label="Start Date"
                required
                value={sapStartDate}
                onChange={(val) => setSapStartDate(val)}
              />

              <CustomCalendar
                label="End Date"
                required
                value={sapEndDate}
                minDate={sapStartDate}
                onChange={(val) => setSapEndDate(val)}
              />

              <div className="pt-2 flex items-center justify-end gap-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsSapModalOpen(false)}
                  className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-bold text-xs hover:bg-slate-50 transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isExportingSap}
                  className="px-5 py-2.5 rounded-xl bg-[#F9C327] hover:bg-[#e2af20] text-slate-900 font-bold text-xs transition-all cursor-pointer shadow-xs disabled:opacity-50 flex items-center gap-2"
                >
                  {isExportingSap ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      Download SAP Excel
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal 2: Export Route Sheet */}
      {isRouteModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl space-y-5 border border-slate-100">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-2xl bg-red-50 flex items-center justify-center text-[#C62828]">
                  <Truck className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-extrabold text-slate-900 tracking-tight">Export Route Sheet</h3>
                  <p className="text-xs text-slate-400 font-medium">Select Depo and Date for route sheet</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsRouteModalOpen(false)}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {routeError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs font-semibold flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0 text-red-500" />
                <span>{routeError}</span>
              </div>
            )}

            <form onSubmit={handleExportRouteSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Select Depo <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={selectedDepoId}
                  onChange={(e) => setSelectedDepoId(e.target.value)}
                  className="w-full border border-slate-300 rounded-xl py-2.5 px-3.5 text-xs font-semibold text-slate-800 outline-none focus:ring-2 focus:ring-[#C62828] bg-white cursor-pointer"
                >
                  <option value="" disabled>-- Select Depo --</option>
                  {deposList.map((depo) => (
                    <option key={depo.id} value={depo.id}>
                      {depo.name}
                    </option>
                  ))}
                </select>
              </div>

              <CustomCalendar
                label="Select Date"
                required
                value={routeDate}
                onChange={(val) => setRouteDate(val)}
              />

              <div className="pt-2 flex items-center justify-end gap-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsRouteModalOpen(false)}
                  className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-bold text-xs hover:bg-slate-50 transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isExportingRoute}
                  className="px-5 py-2.5 rounded-xl bg-[#C62828] hover:bg-[#a81e1e] text-white font-bold text-xs transition-all cursor-pointer shadow-xs disabled:opacity-50 flex items-center gap-2"
                >
                  {isExportingRoute ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      Download Route Sheet
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderReport;
